import { create } from 'zustand';

/**
 * Per-view UI + model state for the Compass Explorer.
 *
 * Holds:
 *  - edgeTierFilter: which edges to show (architecture / data only / full graph).
 *  - sharedDrawerResourceId: the shared resource whose drawer is open.
 *  - focus / zoomLevel / graphVersion: M5 explorer model state. These
 *    are written through commands (`ChangeFocusCommand`,
 *    `ChangeZoomLevelCommand`, `ScrubTimeCommand`) — the setters here
 *    are just the mutation primitives those commands call. Nothing
 *    else should write them directly.
 *
 * State is process-global (not per-diagram-id). Sufficient for today:
 * a user only looks at one Compass Explorer at a time, and URL sync
 * lives at the route level.
 */

export type EdgeTierFilter = 'architecture' | 'data-only' | 'all';

/**
 * What the Explorer is centered on. The whole displayed subgraph is
 * derived from this + the current graph snapshot. `label` is an
 * optional human-friendly name carried alongside the id so the
 * breadcrumb can render "VPC: creately_stage4" instead of the raw id.
 * The drill-click handler populates it from the bubble node's attrs.
 */
export type FocusSpec =
  | { kind: 'overview' }
  | { kind: 'account'; accountId: string; label?: string }
  | { kind: 'vpc'; vpcId: string; radius?: number; label?: string }
  | { kind: 'subnet'; subnetId: string; label?: string }
  | { kind: 'resource'; resourceId: string; radius?: number; label?: string }
  | { kind: 'preset'; presetId: string; label?: string };

/**
 * Level of detail the engine should produce. Not a CSS scale — each
 * step reveals genuinely different content. See the plan doc for
 * what each level renders.
 */
export type ZoomLevel = 1 | 2 | 3 | 4 | 5;

export const DEFAULT_FOCUS: FocusSpec = { kind: 'overview' };
export const DEFAULT_ZOOM: ZoomLevel = 2;
/** Default active lens — `compass-lens-tier` preset resolves to tier colors. */
export const DEFAULT_LENS_ID = 'compass-lens-tier';

interface CompassViewPrefsState {
  // Render prefs
  edgeTierFilter: EdgeTierFilter;
  sharedDrawerResourceId: string | null;

  // Explorer model state (write via commands only)
  focus: FocusSpec;
  zoomLevel: ZoomLevel;
  graphVersion: number | null;
  /**
   * Frames currently expanded in-place. Tracked as an ordered array so
   * `pop()` gives the deepest (most recently expanded) for Esc
   * "collapse one level" semantics. Written via
   * `ToggleFrameExpandedCommand` through `_setExpandedFrameIds`.
   */
  expandedFrameIds: string[];
  /**
   * Ids of aggregated cross-subnet edges that the user has expanded
   * to see their constituent leaf-to-leaf connections. Non-aggregated
   * edges are unaffected. Stored as an array for deterministic
   * ordering; semantically a set.
   */
  expandedEdgeIds: string[];
  /**
   * Global collapse switch. When `true`, cross-subnet leaf edges
   * collapse into single subnet-to-subnet aggregated edges (default).
   * When `false`, every leaf-to-leaf edge is rendered — useful for
   * debugging or when the user wants the full graph.
   */
  collapseEdges: boolean;
  /**
   * Bubble currently highlighted by a findings-row click. Transient —
   * the host integration clears it after a short timeout. Not part of
   * the command framework because it's UI glue, not user-intent.
   */
  highlightedBubbleId: string | null;

  // ── P1.3 active lens (written via ChangeExplorerLensCommand) ─────
  lensId: string;

  // ── P0.1 compare mode (written via EnterCompareModeCommand) ──────
  compareAgainstVersion: number | null;

  // ── P0.2 search (transient UI — not in undo history) ─────────────
  searchQuery: string;
  searchPaletteOpen: boolean;

  // P2.3 filters now live in the shared `viewRulesStore` via
  // `registerDomainPresets(COMPASS_VIEW_PRESETS)`. Delete the old
  // `activeFilters: CompassFilterChip[]` stub — it never had a
  // consumer and duplicated what the shared rules engine provides
  // (primary filter + display rules, Yjs-persisted, smart groups).

  /**
   * Compass-specific quick-filter chips (cost / findings / public /
   * stale / unowned / drift / cross-account / production). Distinct
   * from the shared rules engine — these are stateless toggles whose
   * domain predicates live in `services/explorer/filterChips.ts`.
   * The scene builder reads this set, evaluates the union of active
   * predicates, and stamps `attrs.filterDimmed` on misses. Empty set
   * = no filter active.
   */
  activeFilterChipIds: string[];
  toggleFilterChip: (id: string) => void;
  clearFilterChips: () => void;

  // ── P1.4 narrative / Design & Audit mode ─────────────────────────
  /** Scenario id — read by ExplorerViewService to stamp provisional attrs. */
  activeScenarioId: string | null;
  /** Overlay currently receiving new steps (P1.4b). */
  activeFlowOverlayId: string | null;
  /**
   * Whether the click-an-edge-to-append-a-step mode is active. Flipped
   * via `BeginFlowOverlayAuthoringCommand` / `EndFlowOverlayAuthoringCommand`.
   * When true the overlay layer enables hit-testing on edges.
   */
  flowAuthoringActive: boolean;

  setEdgeTierFilter: (filter: EdgeTierFilter) => void;
  toggleEdgeTier: () => void;
  openSharedDrawer: (resourceId: string) => void;
  closeSharedDrawer: () => void;

  // Transient (non-command) setters — UI may call these directly.
  setSearchQuery: (q: string) => void;
  openSearchPalette: () => void;
  closeSearchPalette: () => void;

  // Low-level setters — commands call these. UI should NOT call them
  // directly; route through CommandManager via the explorer commands.
  _setFocus: (focus: FocusSpec) => void;
  _setZoomLevel: (level: ZoomLevel) => void;
  _setGraphVersion: (version: number | null) => void;
  _setExpandedFrameIds: (ids: string[]) => void;
  toggleExpandedEdge: (edgeId: string) => void;
  setCollapseEdges: (collapse: boolean) => void;
  _setHighlightedBubbleId: (id: string | null) => void;
  _setLensId: (id: string) => void;
  _setCompareAgainstVersion: (v: number | null) => void;
  _setActiveScenarioId: (id: string | null) => void;
  _setActiveFlowOverlayId: (id: string | null) => void;
  _setFlowAuthoringActive: (active: boolean) => void;
}

export const useCompassViewPrefs = create<CompassViewPrefsState>((set, get) => ({
  edgeTierFilter: 'architecture',
  sharedDrawerResourceId: null,

  focus: DEFAULT_FOCUS,
  zoomLevel: DEFAULT_ZOOM,
  graphVersion: null,
  expandedFrameIds: [],
  expandedEdgeIds: [],
  collapseEdges: true,
  highlightedBubbleId: null,

  lensId: DEFAULT_LENS_ID,
  compareAgainstVersion: null,
  searchQuery: '',
  searchPaletteOpen: false,
  activeScenarioId: null,
  activeFlowOverlayId: null,
  flowAuthoringActive: false,
  activeFilterChipIds: [],

  toggleFilterChip: (id) =>
    set((state) => {
      const has = state.activeFilterChipIds.includes(id);
      return {
        activeFilterChipIds: has
          ? state.activeFilterChipIds.filter((x) => x !== id)
          : [...state.activeFilterChipIds, id],
      };
    }),
  clearFilterChips: () => set({ activeFilterChipIds: [] }),

  setEdgeTierFilter: (filter) => set({ edgeTierFilter: filter }),
  toggleEdgeTier: () =>
    set({
      edgeTierFilter: get().edgeTierFilter === 'data-only' ? 'all' : 'data-only',
    }),
  openSharedDrawer: (resourceId) => set({ sharedDrawerResourceId: resourceId }),
  closeSharedDrawer: () => set({ sharedDrawerResourceId: null }),

  setSearchQuery: (q) => set({ searchQuery: q }),
  openSearchPalette: () => set({ searchPaletteOpen: true }),
  closeSearchPalette: () => set({ searchPaletteOpen: false, searchQuery: '' }),

  _setFocus: (focus) => set({ focus }),
  _setZoomLevel: (zoomLevel) => set({ zoomLevel }),
  _setGraphVersion: (graphVersion) => set({ graphVersion }),
  _setExpandedFrameIds: (expandedFrameIds) => set({ expandedFrameIds }),
  toggleExpandedEdge: (edgeId) => {
    const cur = get().expandedEdgeIds;
    const next = cur.includes(edgeId)
      ? cur.filter((id) => id !== edgeId)
      : [...cur, edgeId];
    set({ expandedEdgeIds: next });
  },
  setCollapseEdges: (collapseEdges) => set({ collapseEdges }),
  _setHighlightedBubbleId: (highlightedBubbleId) => set({ highlightedBubbleId }),
  _setLensId: (lensId) => set({ lensId }),
  _setCompareAgainstVersion: (compareAgainstVersion) =>
    set({ compareAgainstVersion }),
  _setActiveScenarioId: (activeScenarioId) => set({ activeScenarioId }),
  _setActiveFlowOverlayId: (activeFlowOverlayId) => set({ activeFlowOverlayId }),
  _setFlowAuthoringActive: (flowAuthoringActive) => set({ flowAuthoringActive }),
}));

/**
 * Stable string key for a FocusSpec. Used to detect no-op focus
 * changes (don't record a history entry if the user clicks the
 * same focus again) and as a URL-friendly identifier.
 */
export function focusKey(focus: FocusSpec): string {
  switch (focus.kind) {
    case 'overview':
      return 'overview';
    case 'account':
      return `account:${focus.accountId}`;
    case 'vpc':
      return focus.radius ? `vpc:${focus.vpcId}:r${focus.radius}` : `vpc:${focus.vpcId}`;
    case 'subnet':
      return `subnet:${focus.subnetId}`;
    case 'resource':
      return focus.radius
        ? `resource:${focus.resourceId}:r${focus.radius}`
        : `resource:${focus.resourceId}`;
    case 'preset':
      return `preset:${focus.presetId}`;
  }
}
